<template>
  <v-ons-page>
    <div class="ja-content-select">
      <div class="page__background" style="background-color: white" />
      <common-header>
        <template #header>
          <span class="header-title">配信するコンテンツを選択</span>
        </template>
      </common-header>
      <div v-show="displayState === DISPLAYSTATE.LOADING_DONE" class="contents-wrapper">
        <div v-if="contents.length" class="contents">
          <ja-content-row
            v-for="content in contents"
            :key="content.contentId"
            :content="content"
            :checkContent="checkContent"
            :selectedContentList="selectedContentList"
            :isCheckable="true"
          />
        </div>
        <empty-content :emptyPageNum="EMPTY_PAGE_NUM.CONTENT" v-else />
      </div>
      <progress-circular v-show="displayState === DISPLAYSTATE.LOADING_PROGRESS" />
      <common-footer class="footer">
        <template #footer>
          <v-ons-row class="footer-button-area">
            <small-button
              class="footer-button footer-back-button"
              title="戻る"
              :disabled="false"
              @click="() => moveBackButton(router)"
            />
            <small-button
              class="footer-button footer-next-button"
              title="次へ"
              :disabled="isNextButtonDisable"
              @click="moveStreamGroupName"
            />
          </v-ons-row>
        </template>
      </common-footer>
    </div>
  </v-ons-page>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

import SmallButton from '@/components/common/SmallButton.vue';
import CommonHeader from '@/components/common/CommonHeader.vue';
import CommonFooter from '@/components/common/CommonFooter.vue';
import ProgressCircular from '@/components/common/ProgressCircular.vue';
import JaContentRow from '@/components/JaStreamGroup/JaContentRow.vue';
import EmptyContent from '@/components/common/EmptyContent.vue';
import { moveBackButton } from '@/composable/common';
import { EMPTY_PAGE_NUM } from '@/composable/constant_common';
import { dataFetchContents } from '@/composable/data_fetch_common';
import { onsAlert } from '@/composable/onsen_common';

const store = useStore();
const router = useRouter();

// ローディング完了フラグ
const isLoadingDone = ref(false);

/**
 * ローディングステータス
 * LOADING_PROGRESS: ロード中
 * LOADING_DONE: ロード完了
 */
const DISPLAYSTATE = {
  LOADING_PROGRESS: 'ロード中',
  LOADING_DONE: 'ロード完了',
};

/**
 * コンテンツ一覧
 */
const contents = ref([]);

/**
 * 選択されたコンテンツ一覧
 */
const selectedContentList = ref([]);

// 最大個数上限などの定数
const MAX_SELECT_COUNT = 15;
const DEFAULT_TIME = 10;

/**
 * 「次へ」ボタン活性判定処理
 */
const isNextButtonDisable = computed(() => {
  const isContentLength = selectedContentList.value.length === 0;
  return isContentLength;
});

/**
 * 画面表示状態
 */
const displayState = computed(() => {
  if (!isLoadingDone.value) return DISPLAYSTATE.LOADING_PROGRESS;
  return DISPLAYSTATE.LOADING_DONE;
});

/**
 * コンテンツ一覧取得処理
 */
const getContents = async () => {
  isLoadingDone.value = false;
  contents.value = await dataFetchContents();
  isLoadingDone.value = true;
};

/**
 * コンテンツ選択処理
 */
const checkContent = (content) => {
  let index = -1;
  // 既に選択されているか判定
  for (let i = 0; i < selectedContentList.value.length; i += 1) {
    if (selectedContentList.value[i].contentId === content.contentId) {
      index = i;
      break;
    }
  }
  if (index === -1) {
    // 未選択→選択
    if (selectedContentList.value.length < MAX_SELECT_COUNT) {
      selectedContentList.value.push(content);
    } else {
      onsAlert('選択できるコンテンツの上限は15個までです。');
    }
  } else {
    // 選択→未選択
    selectedContentList.value.splice(index, 1);
  }
};

/**
 * コンテンツ再生秒数取得処理（コンテンツに動画が含まれている場合は動画の再生秒数を、含まれていない場合はundefinedを返す）
 */
const getContentDurationForMovie = (content) => {
  let contentDuration;
  content.contentMaterialList.forEach((contentMaterial) => {
    if (contentMaterial.materialDuration !== null) {
      contentDuration = contentMaterial.materialDuration;
    }
  });
  return contentDuration;
};

/**
 * コンテンツ再生秒数設定処理（選択したコンテンツについて、コンテンツ再生秒数が設定されていない＆コンテンツに動画が含まれていない場合、コンテンツ再生秒数に10を設定する）
 */
const setContentDurationForImage = () => {
  selectedContentList.value = selectedContentList.value.map((content) => {
    const newContent = { ...content };
    if (
      !content.contentDuration &&
      !content.contentMaterialList.some(
        (contentMaterial) => contentMaterial.materialDuration !== null
      )
    ) {
      newContent.contentDuration = DEFAULT_TIME;
    }
    return newContent;
  });
};

/**
 * 配信グループ名入力画面遷移処理
 */
const moveStreamGroupName = () => {
  setContentDurationForImage();
  store.commit('streamGroup/setStreamGroupContentList', selectedContentList.value);
  router.push('/ja/streamGroup/streamGroupName');
};

onMounted(async () => {
  // サーバからコンテンツ一覧取得し、動画/秒数フラグ追加・型変換
  await getContents();
  contents.value = contents.value.map((content) => ({
    ...content,
    isIncludeMovie: Number(
      content.contentMaterialList.some(
        (contentMaterial) => contentMaterial.materialDuration !== null
      )
    ),
    contentDuration: Number(getContentDurationForMovie(content)),
  }));
  // 登録済みコンテンツ情報を初期選択に反映
  selectedContentList.value = store.getters['streamGroup/getStreamGroupContentList'];
});
</script>

<style scoped>
.ja-content-select {
  margin: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}
.header-title {
  font-size: 18px;
  font-weight: 700;
  text-align: center;
}
.contents-wrapper {
  width: 100%;
}
.contents {
  background-color: #fff;
  padding-top: 10vh;
  padding-bottom: 10vh;
  max-width: 840px;
  inset: 0 0 0 0;
  margin: auto;
}

.footer-button-area {
  height: 100%;
  width: 90%;
}
.footer-button {
  margin: auto;
  padding: 1rem 0;
  text-align: center;
}
.footer-back-button {
  background: #fff;
  border: solid 1px #cccccc;
  color: #222222;
  width: 25%;
}
.footer-next-button {
  box-shadow: 0rem 0.2rem #21963b;
  color: #fff;
  width: 65%;
  cursor: pointer;
}
.footer-next-button:hover {
  opacity: 0.8;
}
.footer {
  height: 9rem;
  flex-direction: column;
}
</style>
